import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from "firebase/analytics";
import {
  getAuth,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  deleteDoc,
  getDoc,
  setDoc,
  query,
  getDocs,
  updateDoc,
  Timestamp,
  orderBy,
  where,
} from 'firebase/firestore';

//
import { FIREBASE_API } from '../config';

// ----------------------------------------------------------------------

// Utility function to generate invite codes
const generateInviteCode = () => {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
};

const firebaseApp = initializeApp(FIREBASE_API);

const analytics = getAnalytics(firebaseApp);

const AUTH = getAuth(firebaseApp);

const DB = getFirestore(firebaseApp);

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIALISE') {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialState,
  method: 'firebase',
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  validateInviteCode: () => Promise.resolve(),
  generateInviteCodes: () => Promise.resolve(),
  getAllInviteCodes: () => Promise.resolve(),
  getAllUsers: () => Promise.resolve(),
  revokeInviteCode: () => Promise.resolve(),
  deleteUser: () => Promise.resolve(),
  updateUser: () => Promise.resolve(),
  submitFeedback: () => Promise.resolve(),
  getAllFeedback: () => Promise.resolve(),
  updateFeedbackStatus: () => Promise.resolve(),
  getFeatureTeasers: () => Promise.resolve(),
  createFeatureTeaser: () => Promise.resolve(),
  updateFeatureTeaser: () => Promise.resolve(),
  deleteFeatureTeaser: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const [profile, setProfile] = useState(null);

  useEffect(
    () =>
      onAuthStateChanged(AUTH, async (user) => {
        if (user) {
          const userRef = doc(DB, 'users', user.uid);

          const docSnap = await getDoc(userRef);

          if (docSnap.exists()) {
            const profileData = docSnap.data();
            const lastSeenAt = Timestamp.now();

            try {
              await updateDoc(userRef, { lastSeenAt });
              setProfile({ ...profileData, lastSeenAt });
            } catch (error) {
              console.error('Failed to update last seen timestamp', error);
              setProfile(profileData);
            }
          }

          dispatch({
            type: 'INITIALISE',
            payload: { isAuthenticated: true, user },
          });
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: { isAuthenticated: false, user: null },
          });
        }
      }),
    [dispatch]
  );

  const login = (email, password) => signInWithEmailAndPassword(AUTH, email, password);

  // Validate invite code
  const validateInviteCode = async (code) => {
      const inviteCode = code.toUpperCase();
      const inviteRef = doc(DB, 'inviteCodes', inviteCode);
      const inviteSnap = await getDoc(inviteRef);

      if (!inviteSnap.exists()) {
        throw new Error('Invalid invite code');
      }

      const inviteData = inviteSnap.data();

      if (inviteData.status === 'used') {
        throw new Error('This invite code has already been used');
      }

      if (inviteData.status === 'revoked') {
        throw new Error('This invite code is no longer valid');
      }

      return { docId: inviteSnap.id, ...inviteData };
  };

  // Register with invite code
  const register = (email, password, firstName, lastName, inviteCode) =>
    validateInviteCode(inviteCode).then(async (inviteData) => {
      const res = await createUserWithEmailAndPassword(AUTH, email, password);

      // Create user document
      const userRef = doc(collection(DB, 'users'), res.user?.uid);
      await setDoc(userRef, {
        uid: res.user?.uid,
        email,
        displayName: [firstName, lastName].filter(Boolean).join(' '),
        role: 'user',
        permissions: ['customer'],
        createdAt: Timestamp.now(),
        lastSeenAt: Timestamp.now(),
        approved: false,
        approvedAt: null,
        approvedBy: null,
      });

      // Mark invite code as used
      const inviteCodeRef = doc(DB, 'inviteCodes', inviteData.docId);
      await updateDoc(inviteCodeRef, {
        status: 'used',
        usedBy: email,
        usedAt: Timestamp.now(),
      });
    });

  const logout = () => signOut(AUTH);

  // Admin functions
  const generateInviteCodes = async (count = 1) => {
    const codes = Array.from({ length: count }, () => ({ code: generateInviteCode(), ref: null }));

    const prepareCodes = async () => {
      await Promise.all(
        codes.map(async (item) => {
          item.ref = doc(DB, 'inviteCodes', item.code);
          item.snapshot = await getDoc(item.ref);
        })
      );

      const collisions = codes.filter((item) => item.snapshot?.exists());
      if (collisions.length === 0) {
        return;
      }

      collisions.forEach((item) => {
        item.code = generateInviteCode();
      });

      await prepareCodes();
    };

    await prepareCodes();

    await Promise.all(
      codes.map((item) =>
        setDoc(item.ref, {
          code: item.code,
          status: 'active',
          createdAt: Timestamp.now(),
          createdBy: state?.user?.uid,
          usedBy: null,
          usedAt: null,
        })
      )
    );

    return codes.map((item) => ({ id: item.ref.id, code: item.code }));
  };

  const getAllInviteCodes = async () => {
    const q = query(collection(DB, 'inviteCodes'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        code: data.code || doc.id,
        ...data,
      };
    });
  };

  const getAllUsers = async () => {
    const q = query(collection(DB, 'users'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const revokeInviteCode = async (codeId) => {
    const codeRef = doc(DB, 'inviteCodes', codeId);
    await updateDoc(codeRef, { status: 'revoked' });
  };

  const deleteUser = async (userId) => {
    const userRef = doc(DB, 'users', userId);
    await deleteDoc(userRef);
  };

  const updateUser = async (userId, updates) => {
    const userRef = doc(DB, 'users', userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  };

  const submitFeedback = async (feedbackData) => {
    const feedbackRef = doc(collection(DB, 'feedback'));
    await setDoc(feedbackRef, {
      ...feedbackData,
      createdAt: Timestamp.now(),
      status: 'new',
    });
    return feedbackRef.id;
  };

  const getAllFeedback = async () => {
    const q = query(collection(DB, 'feedback'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const updateFeedbackStatus = async (feedbackId, status) => {
    const feedbackRef = doc(DB, 'feedback', feedbackId);
    await updateDoc(feedbackRef, {
      status,
      updatedAt: Timestamp.now(),
    });
  };

  // Feature Teasers
  const getFeatureTeasers = async () => {
    const q = query(collection(DB, 'featureTeasers'), where('status', '==', 'active'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const createFeatureTeaser = async (teaser) => {
    const teaserRef = doc(collection(DB, 'featureTeasers'));
    await setDoc(teaserRef, {
      ...teaser,
      status: teaser.status || 'active',
      createdAt: Timestamp.now(),
    });
    return teaserRef.id;
  };

  const updateFeatureTeaser = async (teaserId, updates) => {
    const teaserRef = doc(DB, 'featureTeasers', teaserId);
    await updateDoc(teaserRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  };

  const deleteFeatureTeaser = async (teaserId) => {
    const teaserRef = doc(DB, 'featureTeasers', teaserId);
    await deleteDoc(teaserRef);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'firebase',
        user: {
          id: state?.user?.uid,
          email: state?.user?.email,
          photoURL: state?.user?.photoURL || profile?.photoURL,
          displayName: state?.user?.displayName || profile?.displayName,
          role: profile?.role,
          permissions: profile?.permissions || (profile?.role ? [profile?.role] : []),
          phoneNumber: state?.user?.phoneNumber || profile?.phoneNumber || '',
          country: profile?.country || '',
          address: profile?.address || '',
          state: profile?.state || '',
          city: profile?.city || '',
          zipCode: profile?.zipCode || '',
          about: profile?.about || '',
          isPublic: profile?.isPublic || false,
        },
        login,
        register,
        logout,
        validateInviteCode,
        generateInviteCodes,
        getAllInviteCodes,
        getAllUsers,
        revokeInviteCode,
        deleteUser,
        updateUser,
        submitFeedback,
        getAllFeedback,
        updateFeedbackStatus,
        getFeatureTeasers,
        createFeatureTeaser,
        updateFeatureTeaser,
        deleteFeatureTeaser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider, firebaseApp };
